import { createServerFn } from '@tanstack/react-start';
import { setResponseStatus } from '@tanstack/react-start/server';
import {
    AppwriteException,
    ID,
    MessagingProviderType,
} from 'node-appwrite';
import { z } from 'zod';

import { newsletterTopicId } from '@/lib/newsletter';
import { absoluteUrl } from '@/lib/site-url';
import { createAdminClient, createPublicClient } from '../lib/appwrite';

function throwAppwriteError(error: unknown): never {
    const appwriteError = error as AppwriteException;
    setResponseStatus(appwriteError.code || 500);
    throw {
        message: appwriteError.message || 'Something went wrong',
        status: appwriteError.code || 500,
    };
}

function isConflict(error: unknown) {
    return (error as AppwriteException).code === 409;
}

const subscribeSchema = z.object({
    email: z.email('נא להזין כתובת אימייל תקינה'),
});

const verifySchema = z.object({
    userId: z.string().min(1),
    secret: z.string().min(1),
});

/**
 * Start newsletter signup: send an Appwrite magic URL.
 * The account is created if needed; the user must click the link to verify.
 */
export const subscribeNewsletterFn = createServerFn({ method: 'POST' })
    .validator(subscribeSchema)
    .handler(async ({ data }) => {
        const email = data.email.trim().toLowerCase();
        const url = absoluteUrl('/newsletter/verify');

        try {
            await createPublicClient().account.createMagicURLToken({
                userId: ID.unique(),
                email,
                url,
            });
        } catch (error) {
            throwAppwriteError(error);
        }

        return {
            ok: true as const,
            message:
                'שלחנו קישור אימות לאימייל שלך. לחצו עליו כדי להשלים את ההרשמה לניוזלטר.',
        };
    });

/**
 * Complete magic URL login, then subscribe only if the email is verified.
 * Does not persist a site session - newsletter signup is not an admin login.
 */
export const verifyNewsletterFn = createServerFn({ method: 'POST' })
    .validator(verifySchema)
    .handler(async ({ data }) => {
        let admin;
        try {
            admin = createAdminClient();
        } catch (error) {
            setResponseStatus(500);
            throw {
                message:
                    error instanceof Error
                        ? error.message
                        : 'Server configuration error',
                status: 500,
            };
        }

        let sessionId: string | null = null;

        try {
            const session = await admin.account.createSession({
                userId: data.userId,
                secret: data.secret,
            });
            sessionId = session.$id;

            const user = await admin.users.get({ userId: data.userId });

            if (!user.emailVerification) {
                setResponseStatus(403);
                throw {
                    message:
                        'האימייל לא אומת. לא ניתן להוסיף לניוזלטר בלי אימות.',
                    status: 403,
                };
            }

            let emailTarget =
                user.targets?.find(
                    (target) => target.providerType === MessagingProviderType.Email,
                ) ?? null;

            if (!emailTarget) {
                const listed = await admin.users.listTargets({
                    userId: user.$id,
                });
                emailTarget =
                    listed.targets.find(
                        (target) =>
                            target.providerType === MessagingProviderType.Email,
                    ) ?? null;
            }

            if (!emailTarget) {
                emailTarget = await admin.users.createTarget({
                    userId: user.$id,
                    targetId: ID.unique(),
                    providerType: MessagingProviderType.Email,
                    identifier: user.email,
                });
            }

            const topicId = newsletterTopicId();

            try {
                await admin.messaging.createSubscriber({
                    topicId,
                    subscriberId: user.$id,
                    targetId: emailTarget.$id,
                });
            } catch (error) {
                if (!isConflict(error)) {
                    throwAppwriteError(error);
                }
            }

            return {
                ok: true as const,
                email: user.email,
                message: 'ההרשמה לניוזלטר הושלמה בהצלחה.',
            };
        } catch (error) {
            if (
                error &&
                typeof error === 'object' &&
                'status' in error &&
                'message' in error
            ) {
                throw error;
            }
            throwAppwriteError(error);
        } finally {
            if (sessionId) {
                try {
                    await admin.users.deleteSession({
                        userId: data.userId,
                        sessionId,
                    });
                } catch {
                    // Best-effort cleanup of the verification session.
                }
            }
        }
    });
